import { Box, Paper, Typography, useTheme } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { HelpTooltip } from '@/shared';

export interface OfferConversionDatum {
  /** Axis label, already localised (a weekday short name). */
  label: string;
  submitted: number;
  won: number;
}

export interface OfferConversionChartProps {
  titleId: string;
  title: string;
  submittedLabel: string;
  wonLabel: string;
  helpText?: string;
  helpAriaLabel?: string;
  data: OfferConversionDatum[];
  loading?: boolean;
}

/**
 * Offers submitted against offers won, per day — a provider's hit rate.
 *
 * A separate component from `ActivityBarChart` rather than a variant of it.
 * That one is explicitly single-series and colours its bars with an ordinal
 * ramp indexed by position in the week; adding a second series would break the
 * one rule its whole colour design rests on. Two series need two *identities*,
 * which is the categorical encoding the ramp exists to avoid.
 *
 * So: two colours, grouped side by side, with a legend — because unlike a
 * single series, here the reader genuinely cannot tell which bar is which
 * without being told. Won is the accent; submitted is a muted neutral, so the
 * eye lands on the outcome rather than the effort.
 *
 * Grouped rather than stacked. Stacking would put won *inside* submitted and
 * invite reading the total as "submitted + won", which double-counts every win
 * — a won offer was also a submitted one.
 */
export default function OfferConversionChart({
  titleId,
  title,
  submittedLabel,
  wonLabel,
  helpText,
  helpAriaLabel,
  data,
  loading = false,
}: OfferConversionChartProps) {
  const theme = useTheme();

  const totalSubmitted = data.reduce((sum, d) => sum + d.submitted, 0);
  const totalWon = data.reduce((sum, d) => sum + d.won, 0);
  const rate = totalSubmitted === 0 ? null : Math.round((totalWon / totalSubmitted) * 100);

  return (
    <Paper sx={{ p: 3, height: '100%' }} aria-busy={loading} aria-labelledby={titleId} role="region">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <Typography id={titleId} variant="subtitle1" component="h3" sx={{ fontWeight: 'medium' }}>
          {title}
        </Typography>
        {helpText !== undefined && (
          <HelpTooltip title={helpText} ariaLabel={helpAriaLabel ?? helpText} placement="top" />
        )}
      </Box>

      {/* The number the chart is really about, stated rather than left to be
          eyeballed off seven pairs of bars. Suppressed at zero submitted —
          "0%" reads as a bad hit rate rather than as no bidding. */}
      <Typography color="text.secondary" sx={{ fontSize: '0.78125rem', mb: 1.5 }}>
        {rate === null
          ? `${totalSubmitted} · ${totalWon}`
          : `${totalWon}/${totalSubmitted} · ${rate}%`}
      </Typography>

      <BarChart
        height={260}
        margin={{ top: 16, right: 16, bottom: 32, left: 40 }}
        series={[
          {
            data: data.map((d) => d.submitted),
            label: submittedLabel,
            color: theme.palette.action.disabled,
            valueFormatter: (v) => String(v ?? 0),
          },
          {
            data: data.map((d) => d.won),
            label: wonLabel,
            color: theme.palette.success.main,
            valueFormatter: (v) => String(v ?? 0),
          },
        ]}
        xAxis={[
          {
            scaleType: 'band',
            data: data.map((d) => d.label),
            tickLabelStyle: { fill: theme.palette.text.secondary, fontSize: 11 },
          },
        ]}
        yAxis={[{ tickLabelStyle: { fill: theme.palette.text.secondary, fontSize: 11 } }]}
        borderRadius={4}
        grid={{ horizontal: true }}
        sx={{
          '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': { stroke: theme.palette.divider },
          '& .MuiChartsGrid-line': { stroke: theme.palette.divider, strokeDasharray: 'none' },
        }}
      />
    </Paper>
  );
}
